import { useEffect, useRef } from 'react'

const focusableSelector = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

export function useFocusTrap(ref, active, onClose) {
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  useEffect(() => {
    if (!active || !ref.current) return undefined

    const previousFocus = document.activeElement
    const container = ref.current
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    // Only set initial focus if the focus is outside the container
    if (!container.contains(document.activeElement)) {
      const focusable = [...container.querySelectorAll(focusableSelector)]
      const first = focusable[0]
      window.requestAnimationFrame(() => {
        if (container && !container.contains(document.activeElement)) {
          first?.focus()
        }
      })
    }

    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        event.preventDefault()
        onCloseRef.current?.()
        return
      }

      if (event.key !== 'Tab') return
      const focusable = [...container.querySelectorAll(focusableSelector)]
      if (focusable.length === 0) return
      const first = focusable[0]
      const last = focusable.at(-1)

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last?.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first?.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = previousOverflow
      previousFocus?.focus?.()
    }
  }, [active, ref])
}
